import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { ListShell } from "@/components/shared/ListShell";
import { ListPagination } from "@/components/shared/ListPagination";
import { DeleteConfirmDialog } from "@/components/shared/DeleteConfirmDialog";
import { Input } from "@/components/ui/input";
import { CityFilter } from "@/components/businesses/CityFilter";
import { BusinessRow } from "@/components/businesses/BusinessRow";
import { RejectBusinessDialog } from "@/components/businesses/RejectBusinessDialog";
import { PermissionGate } from "@/components/rbac/PermissionGate";
import { useListQueryState } from "@/hooks/useListQueryState";
import {
  BusinessListItem,
  BusinessStatus,
  useBulkDeleteBusinesses,
  useBusinessList,
  useVerifyBusiness,
} from "@/hooks/useBusinesses";

type TabKey = "all" | "pending" | "approved" | "rejected" | "suspended";

const tabs: Array<{ key: TabKey; label: string; status?: BusinessStatus }> = [
  { key: "all", label: "All" },
  { key: "approved", label: "Approved", status: "APPROVED" },
  { key: "pending", label: "Pending", status: "PENDING" },
  { key: "rejected", label: "Rejected", status: "REJECTED" },
  { key: "suspended", label: "Suspended", status: "SUSPENDED" },
];

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const VALID_TABS = new Set<TabKey>([
  "all",
  "pending",
  "approved",
  "rejected",
  "suspended",
]);

export const BusinessesList = () => {
  const { state, patch } = useListQueryState({
    defaultTab: "all",
    defaultLimit: DEFAULT_PAGE_SIZE,
    allowedLimits: PAGE_SIZE_OPTIONS,
  });
  const tab = (
    VALID_TABS.has(state.tab as TabKey) ? state.tab : "all"
  ) as TabKey;
  const { page, limit, search, city } = state;

  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => setSearchInput(search), [search]);
  useEffect(() => {
    if (searchInput === search) return;
    const t = setTimeout(() => patch({ search: searchInput }), 300);
    return () => clearTimeout(t);
  }, [searchInput, search, patch]);

  const [rejectTarget, setRejectTarget] = useState<BusinessListItem | null>(
    null,
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkDelOpen, setBulkDelOpen] = useState(false);

  useEffect(() => {
    setSelectedIds([]);
  }, [tab, search, city, page]);

  const tabStatus = tabs.find((t) => t.key === tab)?.status;

  const { data, isLoading, error, refetch } = useBusinessList({
    search: search || undefined,
    status: tabStatus,
    city,
    page,
    limit,
  });

  const verify = useVerifyBusiness();
  const bulkDel = useBulkDeleteBusinesses();
  const rows = data?.data ?? [];
  const pagination = data?.pagination;

  const allVisibleSelected =
    rows.length > 0 && rows.every((r) => selectedIds.includes(r.id));
  const someVisibleSelected =
    rows.some((r) => selectedIds.includes(r.id)) && !allVisibleSelected;

  const handleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(rows.map((r) => r.id));
    }
  };

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleBulkDelete = () => {
    bulkDel.mutate(selectedIds, {
      onSuccess: () => {
        toast.success(
          `${selectedIds.length} business${selectedIds.length > 1 ? "es" : ""} deleted`,
        );
        setSelectedIds([]);
        setBulkDelOpen(false);
      },
      onError: (e) => toast.error(e.message),
    });
  };

  useEffect(() => {
    if (
      pagination &&
      pagination.totalPages > 0 &&
      page > pagination.totalPages
    ) {
      patch({ page: pagination.totalPages });
    }
  }, [pagination, page, patch]);

  const handleReject = (reasons: string[], note?: string) => {
    if (!rejectTarget) return;
    verify.mutate(
      { id: rejectTarget.id, status: "REJECTED", reasons, note },
      {
        onSuccess: () => {
          toast.success(`${rejectTarget.businessName} rejected`);
          setRejectTarget(null);
        },
        onError: (e) => toast.error(e.message),
      },
    );
  };

  return (
    <AppLayout title="Businesses">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => patch({ tab: t.key, resetPage: true })}
              className={`px-3 py-1.5 text-sm rounded-btn border ${
                tab === t.key
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <PermissionGate module="businesses" action="create">
          <Link
            to="/businesses/create"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-btn bg-primary text-primary-foreground hover:opacity-90"
          >
            <Plus className="w-4 h-4" /> New business
          </Link>
        </PermissionGate>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search name, phone, email, owner, city…"
            className="pl-8"
          />
        </div>
        <CityFilter
          value={city}
          onChange={(v) => patch({ city: v, resetPage: true })}
        />
      </div>

      {selectedIds.length > 0 && (
        <div className="flex items-center justify-between bg-primary/5 border border-primary/20 rounded-btn px-4 py-2 mb-3 text-sm">
          <span className="font-medium">{selectedIds.length} selected</span>
          <div className="flex items-center gap-2">
            <PermissionGate module="businesses" action="delete">
              <button
                type="button"
                onClick={() => setBulkDelOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-btn bg-destructive text-destructive-foreground hover:opacity-90 text-xs font-medium"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete selected ({selectedIds.length})
              </button>
            </PermissionGate>
            <button
              type="button"
              onClick={() => setSelectedIds([])}
              className="px-3 py-1 rounded-btn text-muted-foreground hover:bg-muted text-xs font-medium"
            >
              Clear selection
            </button>
          </div>
        </div>
      )}

      <ListShell
        isLoading={isLoading}
        error={error}
        isEmpty={rows.length === 0}
        onRetry={() => refetch()}
        emptyTitle="No businesses match"
        emptyDescription="Try clearing filters or creating a new business."
      >
        <div className="bg-card rounded-card border border-border overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-xs text-muted-foreground uppercase">
              <tr>
                <th className="px-3 py-1.5 w-10 text-left font-medium">
                  <input
                    type="checkbox"
                    aria-label="Select all businesses"
                    checked={allVisibleSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someVisibleSelected;
                    }}
                    onChange={handleSelectAll}
                    className="h-4 w-4 rounded border-border cursor-pointer"
                  />
                </th>
                <th className="px-3 py-1.5 text-left font-medium">Business</th>
                <th className="px-3 py-1.5 text-left font-medium">City</th>
                <th className="px-3 py-1.5 text-left font-medium">Phone</th>
                <th className="px-3 py-1.5 text-left font-medium">Reviews</th>
                <th className="px-3 py-1.5 text-left font-medium">Status</th>
                <th className="px-3 py-1.5 text-left font-medium">
                  Subscription
                </th>
                <th className="px-3 py-1.5 text-left font-medium">Visible</th>
                <th className="px-3 py-1.5 text-left font-medium">Featured</th>
                <th className="px-3 py-1.5 text-left font-medium">Joined</th>
                <th className="px-3 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <BusinessRow
                  key={row.id}
                  row={row}
                  onReject={setRejectTarget}
                  isSelected={selectedIds.includes(row.id)}
                  onToggleSelect={handleToggleSelect}
                  onDeleteSuccess={(id) =>
                    setSelectedIds((prev) => prev.filter((x) => x !== id))
                  }
                />
              ))}
            </tbody>
          </table>
        </div>

        <ListPagination
          pagination={pagination}
          page={page}
          rowsShown={rows.length}
          itemLabel="businesses"
          onPageChange={(p) => patch({ page: p })}
          limit={limit}
          onLimitChange={(n) => patch({ limit: n, resetPage: true })}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </ListShell>

      <RejectBusinessDialog
        open={!!rejectTarget}
        onOpenChange={(o) => !o && setRejectTarget(null)}
        businessName={rejectTarget?.businessName}
        onSubmit={handleReject}
        submitting={verify.isPending}
      />

      <DeleteConfirmDialog
        open={bulkDelOpen}
        onOpenChange={setBulkDelOpen}
        title={`Delete ${selectedIds.length} business${selectedIds.length > 1 ? "es" : ""}?`}
        description="This will permanently delete the selected business(es) and all associated data. This action cannot be undone."
        onConfirm={handleBulkDelete}
        loading={bulkDel.isPending}
      />
    </AppLayout>
  );
};

export default BusinessesList;
